import { Box, Button, CloseButton, Drawer, Grid, Text } from "@mantine/core";
import { act, Dispatch, FC, SetStateAction } from "react";
import { DEFAULT_VALUES, FilterComponentProps, FilterControl, FilterType } from "./index";
import { ReactComponent as LeftIcon } from "assets/caretleft.svg";
import SpecialtyTypeFilter from "./SpecialtyTypeFilter";
import UntilFollowUpFilter from "./UntilFollowUpFilter";
import StatusFilter from "./StatusFilter";
import LastCheckInFilter from "./LastCheckInFilter";
import { TYPE_TITLE_MAP } from "../consts";

interface Props {
  activeFilter: FilterType | null;
  onApplyFilter: (f: FilterControl | null) => void;
  filterControl: FilterControl;
  setFilterControl: Dispatch<SetStateAction<FilterControl>>;
  onClose: () => void;
}

const FILTER_CONTENT_MAP = new Map<FilterType, FC<FilterComponentProps>>([
  ["last-check-in", LastCheckInFilter],
  ["status", StatusFilter],
  ["until-follow-up", UntilFollowUpFilter],
  ["specialty-type", SpecialtyTypeFilter]
]);

const FilterModal: FC<Props> = ({ 
  activeFilter, 
  onClose, 
  onApplyFilter,
  filterControl,
  setFilterControl
 }) => {
  
  const ActiveFilterComponent = activeFilter ?
    FILTER_CONTENT_MAP.get(activeFilter) : null;

  const resetFilters = () => {
    let f: FilterControl = filterControl;
    switch (activeFilter) {
      case "last-check-in":
        f = {
          ...f,
          minDaysSinceLastCheckIn: DEFAULT_VALUES.minDaysSinceLastCheckIn,
          maxDaysSinceLastCheckIn: DEFAULT_VALUES.maxDaysSinceLastCheckIn,
          isCheckInFilterActive: DEFAULT_VALUES.isCheckInFilterActive,
        };
        onApplyFilter(f);
      break;
      case "status":
        f = {
          ...f,
          status: [],
        };
      break;
      case "until-follow-up":
        f = {
          ...f,
          minDaysUntilFollowUp: DEFAULT_VALUES.minDaysUntilFollowUp,
          maxDaysUntilFollowUp: DEFAULT_VALUES.maxDaysUntilFollowUp,
        };
      break;
      case "specialty-type":
        f = {
          ...f,
          specialtyType: []
        };
      break;
    }
    setFilterControl(f);
  };

  return (
    <Drawer
      opened={!!activeFilter}
      zIndex={5001}
      size="xl"
      radius="30px 30px 0 0"
      onClose={() => {
        onClose();
      }}
      position="bottom"
      styles={{
        header: {
          display: "none"
        },
        body: {
          height: "100%",
          boxSizing: "border-box"
        }
      }}
    >
      <Box
        p={0}
        pt={40}
        w="100%"
        h="100%"
        pos="relative"
        style={{
          boxSizing: "border-box"
        }}>
        <Box
          pos="absolute"
          top={15}
          w="100%"
          left={10}
          style={{
            boxSizing: "border-box"
          }}
          pr="md">
          <Text fz="sm" mt="xs" mb={5} ml="xl">
            Show <b>only pins</b> on map by
          </Text>
          <Text
            c="#4F7D9C"
            fz="h3"
            fw="bold"
            w="100%"
            style={{
              borderBottom: "1px solid #8B8B8B",
            }}>
            {activeFilter ? TYPE_TITLE_MAP.get(activeFilter) : null}
          </Text>
        </Box>
        <CloseButton
          size="xl"
          pos="absolute"
          top={10}
          right={-10}
          onClick={() => {
            onClose();
          }}
        />
        <LeftIcon
          style={{
            position: "absolute",
            top: "22px",
            left: "5px",
            transform: "scale(1.3)"
          }}
          onClick={() => {
            onApplyFilter(null);
          }} />
        <Box pt="xl" style={{ position: "relative", boxSizing: "border-box", height: "100%"}}>
          {ActiveFilterComponent ? (
            <ActiveFilterComponent
              filterControl={filterControl}
              onFilterControlChange={(f) => {
                if (f) {
                  setFilterControl((prev) => ({
                    ...prev,
                    ...f
                  }));
                }
              }} />
          ) : null}
        </Box>
        <Grid
          pos="absolute"
          w="100%"
          bottom={20}>
          <Grid.Col span={6} style={{ boxSizing: "border-box"}}>
            <Button 
              bg="#FF9D00"
              fullWidth 
              style={{ boxSizing: "border-box"}}
              onClick={() => {
                resetFilters();
              }}>
              {activeFilter === "last-check-in" ? (
                "Set to Default"
              ) : (
                "Toggle All Off"
              )}
            </Button>
          </Grid.Col>
          <Grid.Col span={6} style={{ boxSizing: "border-box"}} >
            <Button 
              bg="#4F7D9C"
              fullWidth 
              style={{ boxSizing: "border-box"}}
              onClick={() => {
                if (activeFilter === "last-check-in") {
                  const enabledCheckInFilterControl: FilterControl = {
                    ...filterControl,
                    isCheckInFilterActive: true,
                  }
                  setFilterControl(enabledCheckInFilterControl);
                  onApplyFilter(enabledCheckInFilterControl);
                } else {
                  onApplyFilter(filterControl);
                }
              }}>
              Apply
            </Button>
          </Grid.Col>
        </Grid>
      </Box>
    </Drawer>
  );
};

export default FilterModal;